import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactory } from "@/core/hooks/useFactory";
import { useAuth } from "@/core/AuthContext";
import { useToast } from "@/shared/hooks/use-toast";
import { fetchActiveSuppliers } from "../api/suppliers";
import { generateClaimNumber, createClaim } from "../api/claims";
import { createInitialSteps } from "../api/steps";
import { createInitialComment } from "../api/comments";
import { queryKeys } from "@/core/lib/queryKeys";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Loader2 } from "lucide-react";

interface CreateClaimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateClaimDialog({ open, onOpenChange }: CreateClaimDialogProps) {
  const { selectedFactory } = useFactory();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [title, setTitle] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [description, setDescription] = useState("");

  const { data: suppliers } = useQuery({
    queryKey: ["mod_8d_suppliers", selectedFactory?.id],
    queryFn: () => fetchActiveSuppliers(selectedFactory!.id),
    enabled: !!selectedFactory?.id,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFactory?.id || !selectedFactory?.tenant_id || !user?.id) {
        throw new Error("Missing context");
      }

      // Generate claim number using API
      const claimNumber = await generateClaimNumber(selectedFactory.tenant_id);

      // Create the claim using API
      const claim = await createClaim({
        tenant_id: selectedFactory.tenant_id,
        factory_id: selectedFactory.id,
        supplier_id: supplierId,
        claim_number: claimNumber,
        title,
        created_by: user.id,
      });

      // Create the 8D steps using API
      await createInitialSteps(selectedFactory.tenant_id, claim.id);

      // Add initial comment with description using API
      if (description.trim()) {
        await createInitialComment(selectedFactory.tenant_id, claim.id, user.id, description);
      }

      // Get supplier info for email
      const supplier = suppliers?.find((s) => s.id === supplierId);
      if (!supplier) throw new Error("Supplier not found");

      // Generate magic link using production domain
      const baseUrl = "https://app.factory-pilot.ai";
      const magicLink = `${baseUrl}/8d/supplier/${claim.id}?token=${claim.magic_token}`;

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke("send-8d-notification", {
        body: {
          to: supplier.contact_email,
          supplierName: supplier.name,
          contactName: supplier.name, // mod_8d.suppliers doesn't have contact_name, use supplier name
          claimNumber: claim.claim_number,
          claimTitle: title,
          magicLink,
          factoryName: selectedFactory.name,
        },
      });

      if (emailError) {
        console.error("Email notification failed:", emailError);
        // Don't throw - claim was created successfully
      }

      return claim;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dClaims(selectedFactory?.id ?? null) });
      toast({
        title: "Claim created",
        description: "The supplier has been notified via email.",
      });
      onOpenChange(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating claim",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setTitle("");
    setSupplierId("");
    setDescription("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create 8D Claim</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier">Supplier *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger>
                <SelectValue placeholder="Select supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers?.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Claim Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Problem Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description of the quality issue..."
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title || !supplierId || createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create & Notify Supplier
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
